import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { signOutAndReset } from '../features/auth/sign-out';
import { Button } from '../shared/ui/Button';
import { PageLoader } from '../shared/ui/PageLoader';

import { useAuth } from './AuthProvider';
import s from './SessionRecovering.module.css';

/**
 * How long to look like an ordinary cold boot before explaining anything.
 *
 * Most reconnects finish in well under a second. Leading with "we can't reach
 * the server" would alarm students over a hiccup they would never otherwise
 * have noticed, so the first few seconds are the same PageLoader every other
 * route shows while it resolves.
 */
const EXPLAIN_AFTER_MS = 6_000;

/**
 * Shown instead of a redirect to /login when the auth server is unreachable but
 * the stored session is still intact — see `unreachable` in AuthProvider.
 *
 * The one job of this screen is to stop the student from re-typing credentials
 * they do not need to re-type. Everything here is therefore reassurance plus a
 * way out; the actual recovery is automatic and needs no action at all.
 */
export function SessionRecovering(): JSX.Element {
  const { retry } = useAuth();
  const navigate = useNavigate();
  const [explain, setExplain] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setExplain(true);
    }, EXPLAIN_AFTER_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, []);

  if (!explain) {
    return <PageLoader label="Reconnecting…" />;
  }

  async function startOver(): Promise<void> {
    setSigningOut(true);
    try {
      // Best effort: signing out needs the server too, so offline this only
      // clears the local caches. That is enough — a fresh sign-in overwrites
      // the stored session either way.
      await signOutAndReset();
    } finally {
      navigate('/login', { replace: true });
    }
  }

  return (
    <div className={s.shell}>
      <div className={s.card} role="status" aria-live="polite">
        <span className={s.spinner} aria-hidden="true" />
        <h1 className={s.title}>Reconnecting…</h1>
        <p className={s.body}>
          You are still signed in — we just can’t reach the server right now. Check your internet
          connection; this screen clears itself the moment it comes back.
        </p>
        <div className={s.actions}>
          <Button variant="secondary" onClick={retry} disabled={signingOut}>
            Try now
          </Button>
          <Button
            variant="ghost"
            loading={signingOut}
            onClick={() => {
              void startOver();
            }}
          >
            Sign in again
          </Button>
        </div>
      </div>
    </div>
  );
}
